import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../../api/apiService";

const schema = yup.object().shape({
  employees: yup.array().of(
    yup.object().shape({
      name: yup.string().required("Name is required"),
      email: yup.string().email("Invalid email").nullable().optional(),
      phone: yup.string().nullable().optional(),
      branch: yup.string().required("Branch is required"),
    })
  ),
});

function BulkEmployee() {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      employees: [{ name: "", email: "", phone: "", branch: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "employees",
  });

  const fetchEmployeeCreate = async () => {
    try {
      const response = await api.get("/employee/create");
      setBranches(response.data.data.branches);
    } catch (error) {
      console.error("Error fetching Employee: ", error);
      toast.error("Failed to fetch Employee");
    }
  };

  useEffect(() => {
    fetchEmployeeCreate();
  }, []);

  useEffect(() => {
    if (branches.length >= 1) {
      const branchId = branches[0].branch_id;
      fields.forEach((_, index) => {
        setValue(`employees.${index}.branch`, branchId);
      });
    }
  }, [branches]);

  const onSubmit = async (data) => {
    try {
      const response = await api.post("bulk-employees/store", data);
      toast.success(response.data.message || "Employees saved successfully!");
      setTimeout(() => {
        navigate("/user/employees");
      }, 500);
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach((err) => {
          toast.error(err);
        });
      } else {
        toast.error("Failed to create employee");
      }
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Bulk Employees</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/home">Home</Link>
            </li>
            <li className="breadcrumb-item active">Bulk Employees</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">Bulk Employees</h5>

                  {fields.map((field, index) => (
                    <div className="row mb-3 align-items-center" key={field.id}>
                      {/* Name */}
                      <div className="col-md-3">
                        <input
                          type="text"
                          className={`form-control ${
                            errors.employees?.[index]?.name ? "is-invalid" : ""
                          }`}
                          placeholder="Name"
                          {...register(`employees.${index}.name`)}
                        />
                        {errors.employees?.[index]?.name && (
                          <div className="invalid-feedback">
                            {errors.employees[index].name.message}
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div className="col-md-3">
                        <input
                          type="email"
                          className={`form-control ${
                            errors.employees?.[index]?.email ? "is-invalid" : ""
                          }`}
                          placeholder="Email"
                          {...register(`employees.${index}.email`)}
                        />
                        {errors.employees?.[index]?.email && (
                          <div className="invalid-feedback">
                            {errors.employees[index].email.message}
                          </div>
                        )}
                      </div>

                      {/* Phone */}
                      <div className="col-md-3">
                        <input
                          type="tel"
                          className={`form-control ${
                            errors.employees?.[index]?.phone ? "is-invalid" : ""
                          }`}
                          placeholder="Phone"
                          {...register(`employees.${index}.phone`)}
                        />
                        {errors.employees?.[index]?.phone && (
                          <div className="invalid-feedback">
                            {errors.employees[index].phone.message}
                          </div>
                        )}
                      </div>

                      <div className="col-md-2">
                        <select
                          className={`form-select ${
                            errors.employees?.[index]?.branch
                              ? "is-invalid"
                              : ""
                          }`}
                          {...register(`employees.${index}.branch`)}
                        >
                          <option value="">Select Branch</option>
                          {branches.map((branch) => (
                            <option
                              key={branch.branch_id}
                              value={branch.branch_id}
                            >
                              {branch.name}
                            </option>
                          ))}
                        </select>
                        {errors.employees?.[index]?.branch && (
                          <div className="invalid-feedback">
                            {errors.employees[index].branch.message}
                          </div>
                        )}
                      </div>

                      {/* Delete button */}
                      <div className="col-md-1 text-center">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => remove(index)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() =>
                        append({
                          name: "",
                          email: "",
                          phone: "",
                          branch:
                            branches.length >= 1 ? branches[0].branch_id : "",
                        })
                      }
                    >
                      Add Row
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}

export default BulkEmployee;
